import { useCallback, useMemo } from 'react';
import { useColorModeValue } from '@chakra-ui/react';
import { format, formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { Options, SeriesAreaOptions, TooltipFormatterContextObject } from 'highcharts';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DateRangeType } from '../../../common/components/DateRangeTypePicker';
import { CHART_DEFAULT_OPTIONS, SERIES_DEFAULT_OPTIONS } from '../../../features/activity/constants';
import { getMaxDateTime, getMinDateTime, getUIConfigForRangeType } from '../../../features/activity/utils';
import { selectSite } from '../../../features/site/siteSlice';
import { getChartTimezoneOffset, getLocaleForDateFns, SupportedLocales } from '../../../utils/common/common-utils';
import { ChargingActivityData, ChargingActivitySessionData } from '../types';

interface Props {
  series: SeriesAreaOptions[];
  selectedDateRangeType: DateRangeType;
  rangeTypeToSelectedPeriod: Record<DateRangeType, Date>;
  chartOpts: {
    height: number; // in pixels
  };
  isCostViewSelected: boolean;
}

export function useChargingHistoryChartData({
  series,
  selectedDateRangeType,
  rangeTypeToSelectedPeriod,
  chartOpts,
  isCostViewSelected,
}: Props) {
  const markerColor = useColorModeValue('#000', '#BCBCBC');
  const { timezone } = useSelector(selectSite);
  const { i18n, t } = useTranslation();

  return useMemo<Options>(() => {
    const isDayView = selectedDateRangeType === DateRangeType.Day;
    const { height } = chartOpts;
    const localeForDateFns = getLocaleForDateFns(i18n.language as SupportedLocales);
    const startDate = rangeTypeToSelectedPeriod[selectedDateRangeType];
    const { xAxisTickFormat, xAxisTickInterval } = getUIConfigForRangeType(selectedDateRangeType);
    const sufix = isCostViewSelected ? '' : ' kW';
    const prefix = isCostViewSelected ? '$' : '';

    return {
      ...CHART_DEFAULT_OPTIONS,
      chart: {
        ...CHART_DEFAULT_OPTIONS.chart,
        height: `${height}px`,
      },
      xAxis: {
        ...CHART_DEFAULT_OPTIONS.xAxis,
        min: getMinDateTime(startDate, selectedDateRangeType, timezone),
        max: getMaxDateTime(startDate, selectedDateRangeType, timezone),
        accessibility: { enabled: true, description: t('Activity.time of the day') },
        tickInterval: xAxisTickInterval,
        labels: {
          style: { fontSize: '11px', color: markerColor },
          formatter: function (data) {
            if (isDayView) {
              return formatInTimeZone(new Date(data.value ?? 0), timezone, xAxisTickFormat);
            } else {
              return format(new Date(data.value ?? 0), xAxisTickFormat, { locale: localeForDateFns });
            }
          },
        },
      },
      yAxis: {
        gridLineDashStyle: 'ShortDash',
        title: {
          text: '',
        },
        accessibility: { enabled: true, description: t('Activity.usage in kw') },
        labels: {
          style: { fontSize: '11px', color: markerColor },
          formatter: function (data) {
            return `${prefix}${data.value}${sufix}`;
          },
        },
      },
      tooltip: {
        ...CHART_DEFAULT_OPTIONS.tooltip,
        backgroundColor: 'none',
        formatter: function () {
          function format(context: TooltipFormatterContextObject) {
            const points = context.points?.map(({ y }) => {
              if (y === 0) return;
              return `<span style="font-size: 16px;">${prefix}${y}${sufix}</span><br/>`;
            });
            return [...(points ?? [])];
          }

          return format(this);
        },
      },
      series: structuredClone(
        series.map((d) => ({
          ...d,
          color: 'rgba(155, 89, 182, 0.8)',
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgba(155, 89, 182, 0.7)'],
              [1, 'rgba(155, 89, 182, 0.0)'],
            ],
          },
          marker: {
            enabled: false,
          },
          lineWidth: 2,
          data: d.data,
        }))
      ),
      time: {
        getTimezoneOffset: (timestamp: number) => getChartTimezoneOffset(timestamp, timezone),
      },
    };
  }, [
    selectedDateRangeType,
    chartOpts,
    isCostViewSelected,
    rangeTypeToSelectedPeriod,
    i18n.language,
    series,
    timezone,
    t,
    markerColor,
  ]);
}

export function useChargingHistorySeries(
  data: ChargingActivityData,
  selectedDateRangeType: DateRangeType,
  isCostViewSelected: boolean
) {
  const { timezone } = useSelector(selectSite);
  const generateContinuousData = useCallback(
    (sessions: ChargingActivitySessionData[], selectedDateRangeType: DateRangeType) => {
      const { xAxisTickInterval } = getUIConfigForRangeType(selectedDateRangeType);
      const isDayView = selectedDateRangeType === DateRangeType.Day;
      const result = [];

      for (let i = 0; i < sessions.length; i++) {
        const current = sessions[i];

        const start = fromZonedTime(current.start, timezone).getTime();
        const end = fromZonedTime(current.end, timezone).getTime();
        const usage = isCostViewSelected ? current.cost : current.usage;

        const startCentered = isDayView ? start : start - xAxisTickInterval / 2;
        const endCentered = isDayView ? end : end - xAxisTickInterval / 2;

        // result.push({ x: startCentered, y: 0 });
        result.push({ x: startCentered, y: usage });
        result.push({ x: endCentered, y: usage });
        // result.push({ x: endCentered, y: 0 });
      }

      return result;
    },
    [isCostViewSelected, timezone]
  );

  const chartData = generateContinuousData(data.chargingSessions, selectedDateRangeType);

  const energyConsumedSeries = useMemo(
    (): SeriesAreaOptions[] => [
      {
        ...SERIES_DEFAULT_OPTIONS,
        id: 'charging-history-chart',
        data: [{ x: 0, y: 0 }, ...chartData.flat()],
        connectNulls: false,
      },
    ],
    [chartData]
  );

  return { energyConsumedSeries };
}
